import React, { useState, useEffect } from 'react';
import DataTable, { DataTableColumn } from "../../../ui/DataTable";
// Aliased: this file already declares a local `Pagination` interface.
import PaginationControl from '../../../common/Pagination';
import { useNavigate } from 'react-router-dom';
import { handleError } from '../../../../utils/errorHandler';
import {
  EnvelopeIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { emailLogsApi } from '../../../../repositories/emailLogs';
import SettingsPanel from '../../../ui/SettingsPanel';
import Button from '../../../ui/Button';

// Pitch with email logs interface
interface PitchWithEmails {
  id: number;
  releaseTitle: string;
  artistName: string;
  createdAt: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const Send: React.FC = () => {
  const navigate = useNavigate();

  // Pitches state
  const [pitches, setPitches] = useState<PitchWithEmails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    fetchPitches(1);
  }, []);

  // Fetch pitches with email logs
  const fetchPitches = async (page: number) => {
    try {
      setLoading(true);
      const response = await emailLogsApi.getPitchesWithEmails({
        page,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'DESC'
      });

      if (response?.data?.success) {
        setPitches(response.data.pitches || []);
        setPagination(response.data.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle view logs - navigate to logs page
  const handleViewLogs = (pitchId: number) => {
    navigate(`/email/logs/${pitchId}`);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    fetchPitches(newPage);
  };

  // Format date short (MM/DD/YYYY)
  const formatDateShort = (dateString: string): string => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const logColumns: DataTableColumn<any>[] = [
    { key: "releaseTitle", header: "Release Title", cell: (p) => <span className="font-medium">{p.releaseTitle || "-"}</span> },
    { key: "artistName", header: "Artist Name", cell: (p) => <span className="text-neutral-600">{p.artistName || "-"}</span> },
    { key: "createdAt", header: "Created Date", cell: (p) => <span className="text-neutral-600">{formatDateShort(p.createdAt)}</span> },
    {
      key: "actions",
      header: "Actions",
      cell: (pitch) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleViewLogs(pitch.id)}
        >
          <EyeIcon className="w-4 h-4" />
          View Logs
        </Button>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Email History"
      description="View all pitches with sent emails and their delivery status"
    >

      {/* Pitches Table */}
      {!loading && pitches.length === 0 ? (
        <div className="text-center py-12">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-black">No emails sent</h3>
          <p className="mt-1 text-sm text-neutral-500">
            Your sent email history will appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <DataTable
              columns={logColumns}
              rows={pitches}
              getRowKey={(pitch: any) => pitch.id}
              loading={loading}
              emptyMessage="No emails sent yet."
            />
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {pitches.map((pitch) => (
              <div key={pitch.id} className="bg-neutral-50 rounded-lg border border-neutral-200 p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm font-medium text-black">{pitch.releaseTitle || '-'}</p>
                    <p className="text-xs text-neutral-600">{pitch.artistName || '-'}</p>
                  </div>
                  <span className="text-xs text-neutral-500">{formatDateShort(pitch.createdAt)}</span>
                </div>
                <button
                  onClick={() => handleViewLogs(pitch.id)}
                  className="w-full mt-2 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                >
                  <EyeIcon className="w-4 h-4" />
                  View Logs
                </button>
              </div>
            ))}
          </div>

          {/* Shared Pagination, same as every other list in the app. */}
          {pagination.totalPages > 1 && (
            <div className="mt-3">
              <PaginationControl
                page={pagination.page}
                totalPages={pagination.totalPages}
                setPage={handlePageChange}
                totalItems={pagination.total}
                limit={pagination.limit}
              />
            </div>
          )}
        </>
      )}
    </SettingsPanel>
  );
};

export default Send;